import http from "axiosSetup";
import { MetadataType, Variants } from "types";
import moment from "moment-timezone";

export interface MeasureAggregate {
  athleteId: string;
  value: number | null;
  /**
   * @param date ISO8601 string for min or max values.
   * It is `null` for average or when no value found
   * for the aggregation period. */
  date: string | null;
  id?: string;
}
export interface DateRangeMeasureAggregate {
  startDate: string;
  endDate: string;
  values: {
    athleteId: string;
    value: number | null;
  }[];
}
export type AggregateFetchType = "Average" | "Max" | "Min";

export interface MeasureAggregateOptions {
  type: Omit<MetadataType, MetadataType.ONE_REP_MAX>;
  /**
   * @param exerciseId only used when type is Exercise */
  exerciseId: string | null;
  /**
   * @param metricId measurement field, e.g height for Anthropometric */
  metricId: string;
  /**
   * @param startDate required but there is no range limit on them so can be used for all time */
  startDate: string;
  /**
   * @param endDate required but there is no range limit on them so can be used for all time */
  endDate: string;
  /**
   * @param variant Optional, only used when type is Exercise.
   * Will return different results depending on valuee.g null on a
   * unilateral will aggregate values for both variants and null */
  variant?: Variants | null;
}

class MeasureAggregateService {
  static getAthletesMin(athleteIds: string[], opts: MeasureAggregateOptions) {
    return http
      .post<MeasureAggregate[]>("measurements/athletes/min", { athleteIds, ...opts })
      .then((response) => response.data);
  }

  static getAthletesMax(athleteIds: string[], opts: MeasureAggregateOptions) {
    return http
      .post<MeasureAggregate[]>("measurements/athletes/max", { athleteIds, ...opts })
      .then((response) => response.data);
  }

  static getAthletesAverage(athleteIds: string[], opts: MeasureAggregateOptions) {
    return http
      .post<MeasureAggregate[]>("measurements/athletes/average", { athleteIds, ...opts })
      .then((response) => response.data);
  }

  static getAthletesLatest(
    athleteIds: string[],
    opts: MeasureAggregateOptions & {
      aggregationType: AggregateFetchType;
    }
  ) {
    const timezone = moment.tz.guess();

    return http
      .post<MeasureAggregate[]>("measurements/athletes/latest-day", { timezone, athleteIds, ...opts })
      .then((response) => response.data);
  }

  static getAthletesMinForDateRanges(
    athleteIds: string[],
    dateRanges: {
      startDate: string;
      endDate: string;
    }[],
    opts: Omit<MeasureAggregateOptions, "startDate" | "endDate">
  ) {
    return http
      .post<DateRangeMeasureAggregate[]>("measurements/athletes/date-ranges/min", { athleteIds, dateRanges, ...opts })
      .then((response) => response.data);
  }

  static getAthletesMaxForDateRanges(
    athleteIds: string[],
    dateRanges: {
      startDate: string;
      endDate: string;
    }[],
    opts: Omit<MeasureAggregateOptions, "startDate" | "endDate">
  ) {
    return http
      .post<DateRangeMeasureAggregate[]>("measurements/athletes/date-ranges/max", { athleteIds, dateRanges, ...opts })
      .then((response) => response.data);
  }

  static getAthletesAverageForDateRanges(
    athleteIds: string[],
    dateRanges: {
      startDate: string;
      endDate: string;
    }[],
    opts: Omit<MeasureAggregateOptions, "startDate" | "endDate">
  ) {
    return http
      .post<
        DateRangeMeasureAggregate[]
      >("measurements/athletes/date-ranges/average", { athleteIds, dateRanges, ...opts })
      .then((response) => response.data);
  }
}

export default MeasureAggregateService;
